import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Menu, Search, Bell, LogOut, User, Sun, Moon, PhoneMissed, ClipboardList, RefreshCw, Send, StopCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import { useLogoutMutation } from '../../api/authApi';
import { useSearchQuery, useNotificationsQuery, useTodayTasksQuery, useActiveSendsQuery } from '../../api/platformApi';
import { useCancelAutomationRunMutation } from '../../api/campaignsApi';
import { baseApi, TAG_TYPES } from '../../api/baseApi';
import { openPatientDetail, openInvoiceDetail } from '../../features/ui/detailPanelSlice';
import ConfirmDialog from '../ConfirmDialog';

const GROUP_LABELS = {
  patients: 'Patients', leads: 'Leads', appointments: 'Appointments',
  invoices: 'Invoices', consultations: 'Consultations',
};

export default function Topbar({ onToggleSidebar }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const refreshToken = useSelector((s) => s.auth?.refreshToken);
  const [doLogout] = useLogoutMutation();
  const [open, setOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('healing.theme') || 'light');
  const ddRef = useRef(null);

  // ── Manual "refresh everything" — re-pulls every screen's data in place, no page reload.
  // Invalidating (not resetApiState()) keeps current data on screen while fresh data loads in
  // the background, so nothing flashes to blank/loading.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    dispatch(baseApi.util.invalidateTags(TAG_TYPES));
    toast.success('Refreshing data…');
    setTimeout(() => setRefreshing(false), 800);
  };

  // ── Global search ──
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);
  const { data: results } = useSearchQuery(debounced, { skip: debounced.length < 2 });

  // ── Notifications ──
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const { data: notif } = useNotificationsQuery(undefined, { pollingInterval: 120000 });
  const { data: todayTasks } = useTodayTasksQuery(undefined, { pollingInterval: 120000 });

  // ── Live "campaign sending" indicator ──
  // Short interval — this is the "is my campaign still going" indicator, so it should feel live.
  // Sending survives this tab (or every tab) closing; this just shows progress when one is open.
  const [sendingOpen, setSendingOpen] = useState(false);
  const sendingRef = useRef(null);
  const { data: activeSends } = useActiveSendsQuery(undefined, { pollingInterval: 8000 });
  const [cancelRun, { isLoading: cancelling }] = useCancelAutomationRunMutation();
  const doCancelRun = async (runId) => {
    try {
      await cancelRun(runId).unwrap();
      toast.info('Cancelling — it will stop after the recipient currently in flight.');
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not cancel.');
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('healing.theme', theme);
  }, [theme]);

  useEffect(() => {
    const handler = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (sendingRef.current && !sendingRef.current.contains(e.target)) setSendingOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = (user?.fullName || user?.userName || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('');

  const groups = useMemo(() => {
    if (!results) return [];
    return Object.entries(GROUP_LABELS)
      .map(([key, label]) => ({ key, label, hits: results[key] ?? [] }))
      .filter((g) => g.hits.length > 0);
  }, [results]);
  const totalHits = groups.reduce((n, g) => n + g.hits.length, 0);

  const handleLogout = async () => {
    setConfirmLogout(false);
    if (refreshToken) {
      try { await doLogout({ refreshToken }).unwrap(); } catch { /* best-effort */ }
    }
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  // Patients and invoices open as an in-place overlay (see detailPanelSlice) so search never
  // navigates the user away from wherever they currently are. Other groups (leads, appointments,
  // consultations) still navigate to their own page's native ?openId= deep link.
  const openHit = (hit, groupKey) => {
    setSearchOpen(false);
    setTerm('');
    if (groupKey === 'patients') { dispatch(openPatientDetail(hit.id)); return; }
    if (groupKey === 'invoices') { dispatch(openInvoiceDetail(hit.id)); return; }
    if (hit.route) navigate(hit.route);
  };

  const notifCount = notif?.count ?? 0;
  const notConnectedCount = notif?.notConnectedLeads ?? 0;

  return (
    <header className="topbar">
      <button className="btn-icon" onClick={onToggleSidebar} aria-label="Toggle sidebar"><Menu size={18} /></button>

      <div className="search-field" ref={searchRef} style={{ position: 'relative' }}>
        <Search size={16} className="si" />
        <input
          className="form-control"
          placeholder="Search patients, leads, invoices…"
          value={term}
          onChange={(e) => { setTerm(e.target.value); setSearchOpen(true); }}
          onFocus={() => setSearchOpen(true)}
        />
        {searchOpen && debounced.length >= 2 && (
          <div className="dd-menu" style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 420, overflowY: 'auto', marginTop: 6 }}>
            {totalHits === 0 && <div className="dd-item" style={{ color: 'var(--text-muted)' }}>No results for “{debounced}”.</div>}
            {groups.map((g) => (
              <div key={g.key}>
                <div style={{ padding: '8px 12px 4px', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>{g.label}</div>
                {g.hits.map((h) => (
                  <div key={h.id} className="dd-item" onClick={() => openHit(h, g.key)} style={{ display: 'block' }}>
                    <div style={{ fontWeight: 600 }}>{h.title}</div>
                    {h.subtitle && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{h.subtitle}</div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          className="btn-icon"
          aria-label="Refresh data"
          title="Refresh all data on screen — pulls the latest from the database without reloading the page"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw size={18} className={refreshing ? 'spin' : undefined} />
        </button>

        <button className="btn-icon" aria-label="Toggle theme" onClick={() => setTheme((t) => t === 'dark' ? 'light' : 'dark')}>
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Live "campaign sending" indicator — sending itself runs in the background regardless of
            whether any tab is open; this just surfaces progress while one happens to be, and lets
            staff stop one from anywhere in the app. */}
        {(activeSends?.length ?? 0) > 0 && (() => {
          const totalDone = activeSends.reduce((n, r) => n + r.sentCount + r.failedCount + r.skippedCount, 0);
          const totalOf = activeSends.reduce((n, r) => n + r.audienceCount, 0);
          return (
            <div className="dropdown" ref={sendingRef} style={{ position: 'relative' }}>
              <button
                className="btn-icon"
                aria-label={`Sending: ${totalDone} of ${totalOf} done`}
                title={`Sending — ${totalDone.toLocaleString()} of ${totalOf.toLocaleString()} done. Click for details.`}
                onClick={() => setSendingOpen((o) => !o)}
                style={{
                  width: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 10px', color: 'var(--blue-700, #1d4ed8)',
                }}
              >
                <Send size={15} className="spin" style={{ animationDuration: '2.2s' }} />
                <span style={{ fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {totalDone.toLocaleString()}/{totalOf.toLocaleString()}
                </span>
              </button>
              {sendingOpen && (
                <div className="dd-menu" style={{ position: 'absolute', top: '100%', right: 0, width: 320, marginTop: 6 }}>
                  <div style={{ padding: '8px 12px', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>
                    Sending now
                  </div>
                  {activeSends.map((r) => {
                    const done = r.sentCount + r.failedCount + r.skippedCount;
                    const pct = r.audienceCount > 0 ? Math.min(100, Math.round((done / r.audienceCount) * 100)) : 0;
                    return (
                      <div key={r.id} className="dd-item" style={{ display: 'block', cursor: 'default' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.automationName}
                          </div>
                          <button
                            className="btn-icon-sm"
                            title="Stop this send — whoever's already been messaged keeps their claim, so a later run won't repeat them"
                            disabled={cancelling}
                            onClick={() => doCancelRun(r.id)}
                          >
                            <StopCircle size={14} color="var(--danger, #dc2626)" />
                          </button>
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 4px' }}>
                          {done.toLocaleString()} of {r.audienceCount.toLocaleString()} done
                          {r.failedCount > 0 && ` · ${r.failedCount.toLocaleString()} failed`}
                        </div>
                        <div style={{ height: 5, borderRadius: 999, background: 'var(--border)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--blue-500, #3b82f6)', borderRadius: 999, transition: 'width 0.4s' }} />
                        </div>
                      </div>
                    );
                  })}
                  <div className="dd-divider" />
                  <div className="dd-item" onClick={() => { setSendingOpen(false); navigate('/campaigns'); }}>
                    Open Campaigns →
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Today's Tasks badge — open count of "who do I contact today". */}
        <button
          className="btn-icon"
          aria-label={`${todayTasks?.count ?? 0} task(s) today`}
          title={`${todayTasks?.count ?? 0} open task(s) for today — click to open the Action Center`}
          onClick={() => navigate('/tasks')}
          style={{ position: 'relative' }}
        >
          <ClipboardList size={18} />
          {(todayTasks?.count ?? 0) > 0 && (
            <span style={{
              position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px',
              background: 'var(--blue-500, #3b82f6)', color: '#fff', borderRadius: 999, fontSize: '0.62rem',
              fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{todayTasks.count}</span>
          )}
        </button>

        {/* Not-Connected leads badge — new leads nobody has reached yet. */}
        <button
          className="btn-icon"
          aria-label={`${notConnectedCount} lead(s) not connected`}
          title={`${notConnectedCount} lead(s) in Not Connected — click to view`}
          onClick={() => navigate('/leads?status=NotConnected')}
          style={{ position: 'relative' }}
        >
          <PhoneMissed size={18} />
          {notConnectedCount > 0 && (
            <span style={{
              position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px',
              background: 'var(--amber-500, #f59e0b)', color: '#fff', borderRadius: 999, fontSize: '0.62rem',
              fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{notConnectedCount}</span>
          )}
        </button>

        <div className="dropdown" ref={notifRef} style={{ position: 'relative' }}>
          <button className="btn-icon" aria-label="Notifications" onClick={() => setNotifOpen((o) => !o)} style={{ position: 'relative' }}>
            <Bell size={18} />
            {notifCount > 0 && (
              <span style={{
                position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px',
                background: 'var(--red-500)', color: '#fff', borderRadius: 999, fontSize: '0.62rem',
                fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{notifCount}</span>
            )}
          </button>
          {notifOpen && (
            <div className="dd-menu" style={{ position: 'absolute', top: '100%', right: 0, width: 320, maxHeight: 400, overflowY: 'auto', marginTop: 6 }}>
              <div style={{ padding: '8px 12px', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>Notifications</div>
              {(notif?.items ?? []).length === 0 && <div className="dd-item" style={{ color: 'var(--text-muted)' }}>You're all caught up.</div>}
              {(notif?.items ?? []).map((n, i) => (
                <div key={i} className="dd-item" onClick={() => { setNotifOpen(false); if (n.route) navigate(n.route); }} style={{ display: 'block' }}>
                  <div style={{ fontWeight: 600 }}>{n.title}</div>
                  {n.subtitle && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{n.subtitle}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dropdown" ref={ddRef}>
          <button className="topbar-user" onClick={() => setOpen((o) => !o)}>
            <div className="avatar av-sm">{initials}</div>
            <div className="topbar-user-text">
              <div className="topbar-user-name">{user?.fullName || user?.userName || '—'}</div>
              <div className="topbar-user-role">{user?.roles?.[0] || 'Member'}</div>
            </div>
          </button>
          {open && (
            <div className="dd-menu">
              <div className="dd-item" onClick={() => { setOpen(false); navigate('/settings'); }}><User size={16} /> Profile</div>
              <div className="dd-divider" />
              <div className="dd-item danger" onClick={() => { setOpen(false); setConfirmLogout(true); }}><LogOut size={16} /> Sign out</div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        title="Sign out?"
        message="You will need to log in again to access the CRM."
        confirmLabel="Sign out"
        variant="danger"
        onConfirm={handleLogout}
        onCancel={() => setConfirmLogout(false)}
      />
    </header>
  );
}
